import _ from 'lodash';
import { Avatar } from '@lifeomic/chroma-react/components/Avatar';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { connect } from 'react-redux';
import { alpha } from '@mui/material/styles';
import { injectIntl, FormattedMessage, defineMessages } from 'react-intl';
import {
  Popover,
  PopoverActions,
} from '@lifeomic/chroma-react/components/Popover';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { userSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/user';
import { withStyles } from '@lifeomic/chroma-react/styles';
import { configSelectors } from '@lifeomic/phc-web-toolkit/src/redux/modules/config';
import clsx from 'clsx';
import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React from 'react';
import urls from '../../../util/urls';
import getIsInFedEnvironment from '@lifeomic/phc-web-toolkit/src/util/getIsInFedEnvironment';
import { withAuth } from 'react-oidc-context';

const styles = theme => ({
  root: {
    marginLeft: theme.spacing(1.5),
  },
  paper: {
    boxShadow: '0px 10px 64px rgba(0, 0, 0, 0.2)',
    borderRadius: 4,
  },
  popoverContainer: {
    minWidth: '15rem',
    '&:focus': {
      outline: 'none',
    },
  },
  multiplePopoverActions: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(1),
      width: '100%',
    },
  },
  cursorPointer: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  emailText: {
    width: '100%',
    '&:hover': {
      color: alpha(theme.palette.text.dark, 0.85),
    },
  },
});

const messages = defineMessages({
  userMenu: {
    id: 'userMenu.userMenu',
    defaultMessage: 'User Menu',
  },
  nameUserMenu: {
    id: 'userMenu.nameUserMenu',
    defaultMessage: '{displayName} User Menu',
  },
});

class UserMenu extends React.PureComponent {
  oidcLogOut = () => {
    if (this.props.oidcAuthEnabled) {
      this.props.auth.removeUser();
      this.props.auth.revokeTokens();
    }
  };

  handleLogoutClick = popover => e => {
    this.oidcLogOut();
    this.props.logout();
    popover.hide();
  };

  handleGlobalLogoutClick = popover => e => {
    this.oidcLogOut();
    this.props.globalLogout();
    popover.hide();
  };

  handleProfileClick = popover => e => {
    this.props.push(urls.app.userProfile());
    popover.hide();
  };

  render() {
    const {
      env,
      user,
      classes,
      intl: { formatMessage },
    } = this.props;
    const displayName = _.get(
      user,
      'response.profile.name',
      _.get(user, 'response.id', ''),
    );
    const picture = _.get(user, 'response.profile.picture');
    const email = _.get(user, 'response.profile.email');
    const isInGovEnv = env === 'dev' || getIsInFedEnvironment();

    return (
      <Popover
        aria-label={formatMessage(messages.userMenu)}
        placement="bottom-end"
        anchorElement={
          <Avatar
            size={0}
            aria-label={
              displayName
                ? formatMessage(messages.nameUserMenu, { displayName })
                : formatMessage(messages.userMenu)
            }
            src={picture}
            name={displayName}
            className={classes.root}
          ></Avatar>
        }
      >
        {({ popover }) => (
          <React.Fragment>
            <Box
              className={classes.popoverContainer}
              align="center"
              gap={1.5}
              padding={2}
            >
              <Avatar
                role="link"
                size={1}
                src={picture}
                onClick={this.handleProfileClick(popover)}
                name={displayName}
              />
              <Text
                size="subbody"
                className={clsx(classes.cursorPointer, classes.emailText)}
                onClick={this.handleProfileClick(popover)}
                role="link"
              >
                {email}
              </Text>
            </Box>
            <PopoverActions
              justify="center"
              className={isInGovEnv ? classes.multiplePopoverActions : ''}
            >
              <Button
                variant="contained"
                onClick={this.handleLogoutClick(popover)}
              >
                {isInGovEnv ? (
                  <FormattedMessage
                    id="usermenu.gov.signout"
                    defaultMessage="Sign Out (this browser)"
                  />
                ) : (
                  <FormattedMessage
                    id="usermenu.signout"
                    defaultMessage="Sign Out"
                  />
                )}
              </Button>
              {isInGovEnv && (
                <Button
                  variant="outlined"
                  onClick={this.handleGlobalLogoutClick(popover)}
                >
                  <FormattedMessage
                    id="usermenu.gov.global.signout"
                    defaultMessage="Sign Out (all browsers and devices)"
                  />
                </Button>
              )}
            </PopoverActions>
          </React.Fragment>
        )}
      </Popover>
    );
  }
}

UserMenu.propTypes = {
  user: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  globalLogout: PropTypes.func.isRequired,
  push: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  activeUserId: userSelectors.selectActiveUserId(state),
  env: configSelectors.selectConfigState(state).env,
  oidcAuthEnabled: configSelectors.selectConfigState(state).oidcAuthEnabled,
});

export default compose(
  injectIntl,
  withStyles(styles),
  connect(mapStateToProps),
)(withAuth(UserMenu));
